import { useEffect, useState } from 'react';
import { getSyncEngine } from '../local';
import type { SyncStatus } from '../local/types';
import { useTaskOutbox } from './useTaskOutbox';
import { Link } from 'react-router';
import type { OutboxItem } from '../local/taskOutbox';

export function useSyncStatus(): SyncStatus {
  const [status, setStatus] = useState<SyncStatus>(() =>
    getSyncEngine().getStatus(),
  );
  useEffect(() => getSyncEngine().subscribe(setStatus), []);
  return status;
}

/**
 * Quiet sync indicator (spec §8): never a blocking dialog. Renders nothing
 * when everything is synced; otherwise a small status chip.
 */
export function SyncIndicator() {
  const status = useSyncStatus();
  const outbox = useTaskOutbox();

  const tasksChip = <TaskOutboxChip items={outbox.items} />;
  if (status.state === 'synced' && status.pendingCount === 0) return tasksChip;

  // Whole words, no trailing "…" — "Syncing 12…" read as a cut-off label (owner 2026-10-28).
  const changes = `${status.pendingCount} change${status.pendingCount === 1 ? '' : 's'}`;
  const label =
    status.state === 'offline'
      ? `Offline — ${changes} saved on this device`
      : status.state === 'syncing'
        ? `Syncing ${changes}`
        : status.state === 'error'
          ? `Sync retrying — ${changes} waiting`
          : `${changes} waiting to sync`;

  const auditChip = (
    <span
      className={`sync-chip sync-${status.state}`}
      title={status.lastError ?? 'Changes are saved on this device and sync automatically.'}
    >
      {status.state === 'offline' ? '⬤' : '↻'} {label}
    </span>
  );
  return (
    <>
      {auditChip}
      {tasksChip}
    </>
  );
}

/**
 * Task comments/files waiting on this device (B4): "2 task items waiting",
 * or "1 task item couldn’t be sent" (links to the Tasks page, which lists them).
 */
function TaskOutboxChip({ items }: { items: readonly OutboxItem[] }) {
  if (items.length === 0) return null;
  const failed = items.filter((i) => i.state === 'failed').length;
  const waiting = items.length - failed;
  const n = (k: number) => `${k} task item${k === 1 ? '' : 's'}`;
  const label = failed > 0 ? `${n(failed)} couldn’t be sent` : `${n(waiting)} waiting to send`;
  return (
    <Link
      to="/tasks"
      className={`sync-chip ${failed > 0 ? 'sync-error' : 'sync-offline'}`}
      title="Task comments and files saved on this device — they send when you have signal. See the Tasks page."
    >
      {failed > 0 ? '⚠' : '⬤'} <span className="sync-long">{label}</span>
      {/* Phones (UI review 3, S4): the long label was cut to "2 task items waiti…". Hidden from text reads (aria, e2e). */}
      <span className="sync-short" aria-hidden="true">
        {failed > 0 ? `${failed} not sent` : `${waiting} waiting to send`}
      </span>
    </Link>
  );
}
